import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { cloneDeep } from 'lodash';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LandAreaFormGroup } from 'src/app/components/land-areas/form-group';
import { createParcelIdentificationFormGroup } from 'src/app/components/parcel-identification/form-group';
import { AssetClassLandAreaModel } from 'src/app/core/asset_class';
import { AssetClassSizeModel } from 'src/app/core/comparable';
import { AssetClassBuilding } from 'src/app/core/comparable/_models/asset-class-building.model';
import { CoordinatePoint } from 'src/app/core/v3/coordinate-points';
import { LandArea, mapAssetClassLandAreaComponentToLandArea, mapLandAreaToAssetClassLandAreaComponent } from 'src/app/core/v3/land-area';
import { AcGroundsComponent } from '../ac-grounds/ac-grounds.component';
import { BuildingInternalAspectsComponent } from '../building-internal-aspects/building-internal-aspects.component';
import { BuildingSectionComponent } from '../building-section/building-section.component';
import { ExternalAspectsComponent } from '../external-aspects/external-aspects.component';
import { ParcelIdentifactionSectionComponent } from '../parcel-identifaction-section/parcel-identifaction-section.component';
import { ServicesAndInfrastructuresComponent } from '../services-and-infrastructures/services-and-infrastructures.component';

@Component({
  selector: 'kt-building-form',
  templateUrl: './building-form.component.html',
  styleUrls: ['./building-form.component.scss']
})
export class BuildingFormComponent implements OnInit, OnDestroy {
  @Input() readonly: boolean = false;
  @Input() isComplexForm: boolean = false;
  @Input() acBuilding: AssetClassBuilding;

  @Input() centerLat: number;
  @Input() centerLng: number;
  @Input() buildingId: number = null;
  @Input() buildingPictures: any[] = [];
  @Input() buildingPicturePath: string = null;
  @Input() parent: 'Comparable' | 'Inspection' = 'Comparable'
  @Input() building$: Observable<any>
  @Output() centerChange: EventEmitter<any> = new EventEmitter();
  @Output() picChange: EventEmitter<{pictures: any[], picture: any}> = new EventEmitter();

  @ViewChild(BuildingSectionComponent, {static: false})
  buildingSectionComponent: BuildingSectionComponent;
  @ViewChild(ExternalAspectsComponent, {static: false})
  externalAspectComponent: ExternalAspectsComponent;
  @ViewChild(BuildingInternalAspectsComponent, {static: false})
  internalAspectComponent: BuildingInternalAspectsComponent;
  @ViewChild(AcGroundsComponent, {static: false})
  acGroundsComponent: AcGroundsComponent;
  @ViewChild(ServicesAndInfrastructuresComponent, {static: false})
  servicesAndInfraComponent: ServicesAndInfrastructuresComponent;

  errorFields = [];

  serviceAndInfraData;
  externalAspectData;
  internalAspectData;
  grounds;
  sizesSubject = new BehaviorSubject<AssetClassSizeModel[]>([]);
  hasParcelChecked = false;

  parcelIdentificationForm = createParcelIdentificationFormGroup({
    addInfoOnLand: false,
    boundaryType: 1,
    landParcelName: null,
    coordinateReferenceSystemId: null,
    landArea: {
      planningStatusId: null,
      qosDegradationId: null,
      envConsiderations: null,
      otherConsiderations: null
    }
  })   
  private _points$ = new BehaviorSubject<CoordinatePoint[]>([])
  coordinatePoints$ = this._points$.asObservable()
  private _landAreas$ = new BehaviorSubject<LandArea[]>([])
  landAreas$ = this._landAreas$.asObservable()
  private _onDestroy$ = new Subject<void>()

  constructor() { }

  ngOnInit(): void {
    if (this.acBuilding == null) {
      this.acBuilding = new AssetClassBuilding();
    }
    this.serviceAndInfraData = this.acBuilding.serviceAndInfra;
    this.externalAspectData = this.acBuilding.externalAspectData;
    this.internalAspectData = this.acBuilding.internalAspectData;
    this.grounds = this.acBuilding.grounds;
    this.sizesSubject.next(this.acBuilding.sizes);

    this._points$.next(this.acBuilding.parcelIdentification.points.map(p => ({
      latitude: Number(p.lat),
      longitude: Number(p.lng)
    })))
    this._landAreas$.next(this.acBuilding.parcelIdentification.landAreaComponents.map(item => mapAssetClassLandAreaComponentToLandArea(item)))
    this.parcelIdentificationForm.patchValue({
      add_info_on_land: this.acBuilding.parcelIdentification.add_info_on_land,
      boundary_type: this.acBuilding.parcelIdentification.boundary_type_id,
      land_parcel_name: this.acBuilding.parcelIdentification.land_parcel_name,
      coordinate_reference_system_id: this.acBuilding.parcelIdentification.coordinate_reference_system_id,
      land_area: {
        planning_status_id: this.acBuilding.parcelIdentification.landArea.planning_status_id,
        qos_degradation_id: this.acBuilding.parcelIdentification.landArea.qos_degradation_id,
        env_considerations: this.acBuilding.parcelIdentification.landArea.env_considerations,
        other_considerations: this.acBuilding.parcelIdentification.landArea.other_considerations
      }
    })

    this.parcelIdentificationForm.controls.add_info_on_land.valueChanges.pipe(
      takeUntil(this._onDestroy$)
    ).subscribe(value => {
      this.hasParcelChecked = value
    })
  }

  ngOnDestroy(): void {
    this._onDestroy$.next()
    this._onDestroy$.complete()
  }


  validate(): boolean {
    let errorFields = [];
    let hasError = false;
    if (this.buildingSectionComponent && this.buildingSectionComponent.validate()) {
      hasError = true;
      errorFields = [...errorFields, ...this.buildingSectionComponent.errorFields];
    }

    this.errorFields = errorFields;
    return hasError;
  }

  onCenterChange(event: any) {
    this.centerChange.emit(event);
  }
  onPicChange(event: any) {
    this.picChange.emit(event);
  }

  onBuildingChange(event) {
    const b = cloneDeep(event);
    this.serviceAndInfraData = {...b.serviceAndInfraData, id: null};
    this.externalAspectData = {...b.externalAspectData, id: null};
    this.internalAspectData = {...b.internalAspectData, id: null};
    this.grounds = {...b.grounds, id: null};
    this.sizesSubject.next(b.sizes.map(s => ({...s, id: null})));
    this.parcelIdentificationForm.patchValue({
      add_info_on_land: !!b.parcelIdentification.add_info_on_land,
      boundary_type: b.parcelIdentification.boundary_type_id,
      land_parcel_name: b.parcelIdentification.land_parcel_name,
      coordinate_reference_system_id: b.parcelIdentification.coordinate_reference_system_id,
      land_area: {
        planning_status_id: b.parcelIdentification.landArea.planning_status_id,
        qos_degradation_id: b.parcelIdentification.landArea.qos_degradation_id,
        env_considerations: b.parcelIdentification.landArea.env_considerations,
        other_considerations: b.parcelIdentification.landArea.other_considerations
      }
    })
    this._points$.next(b.parcelIdentification.points.map(p => ({
      latitude: Number(p.lat),
      longitude: Number(p.lng)
    })))
    this._landAreas$.next(b.parcelIdentification.landAreaComponents.map(item => mapAssetClassLandAreaComponentToLandArea(item)))
  }

  onBuildingCleared() {
    const id = this.acBuilding.id;
    this.acBuilding = new AssetClassBuilding();
    this.acBuilding.id = id;
    this.serviceAndInfraData = this.acBuilding.serviceAndInfra;
    this.externalAspectData = this.acBuilding.externalAspectData;
    this.internalAspectData = this.acBuilding.internalAspectData;
    this.grounds = this.acBuilding.grounds;
    this._points$.next(this.acBuilding.parcelIdentification.points.map(p => ({
      latitude: Number(p.lat),
      longitude: Number(p.lng)
    })))
    this._landAreas$.next(this.acBuilding.parcelIdentification.landAreaComponents.map(item => mapAssetClassLandAreaComponentToLandArea(item)))
    this.parcelIdentificationForm.patchValue({
      add_info_on_land: this.acBuilding.parcelIdentification.add_info_on_land,
      boundary_type: this.acBuilding.parcelIdentification.boundary_type_id,
      land_parcel_name: this.acBuilding.parcelIdentification.land_parcel_name,
      coordinate_reference_system_id: this.acBuilding.parcelIdentification.coordinate_reference_system_id,
      land_area: {
        planning_status_id: this.acBuilding.parcelIdentification.landArea.planning_status_id,
        qos_degradation_id: this.acBuilding.parcelIdentification.landArea.qos_degradation_id,
        env_considerations: this.acBuilding.parcelIdentification.landArea.env_considerations,
        other_considerations: this.acBuilding.parcelIdentification.landArea.other_considerations
      }
    })
    this.sizesSubject.next(this.acBuilding.sizes);
  }

  onParcelChecked(val: boolean) {
    this.hasParcelChecked = val;
  }

  getData() {
    const _building = new AssetClassBuilding();

    _building.id = this.acBuilding.id;

    const landArea = new AssetClassLandAreaModel()
    const landAreaForm = this.parcelIdentificationForm.controls.land_area as LandAreaFormGroup
    landArea.clear()
    landArea.id = this.acBuilding.parcelIdentification.landArea.id
    landArea.planning_status_id = landAreaForm.controls.planning_status_id.value
    landArea.qos_degradation_id = landAreaForm.controls.qos_degradation_id.value
    landArea.env_considerations = landAreaForm.controls.env_considerations.value
    landArea.other_considerations = landAreaForm.controls.other_considerations.value
    _building.parcelIdentification.add_info_on_land = this.parcelIdentificationForm.controls.add_info_on_land.value
    _building.parcelIdentification.land_parcel_name = this.parcelIdentificationForm.controls.land_parcel_name.value
    _building.parcelIdentification.coordinate_reference_system_id = this.parcelIdentificationForm.controls.coordinate_reference_system_id.value
    _building.parcelIdentification.boundary_type_id = this.parcelIdentificationForm.controls.boundary_type.value
    _building.parcelIdentification.points = this._points$.value.map(item => ({lat: item.latitude, lng: item.longitude}));
    _building.parcelIdentification.landArea = landArea;
    _building.parcelIdentification.landAreaComponents = this._landAreas$.value.map(item => mapLandAreaToAssetClassLandAreaComponent(item)) 
    if (this.buildingSectionComponent) {
      const _data = this.buildingSectionComponent.getDataForComparable();
      _building.infoSection.building_id = _data.building_id;
      _building.infoSection.building_name = _data.building_name;
      _building.infoSection.completion_year = _data.completion_year;
      _building.infoSection.is_part_of_property = _data.is_part_of_property;
      _building.infoSection.info_about_property = _data.info_about_property;
      _building.infoSection.building_type = _data.building_type;
      _building.infoSection.building_type_comment = _data.building_type_comment;
      _building.infoSection.building_grade_id = _data.building_grade_id;
      _building.infoSection.energy_efficiency_grade_id = _data.energy_efficiency_grade_id;
      _building.infoSection.developer = _data.developer;
      _building.infoSection.anchor_tenant = _data.anchor_tenant;
      _building.infoSection.foundation_type_id = _data.foundation_type_id;
      _building.infoSection.building_description = _data.building_description;
    }
    if (this.servicesAndInfraComponent) {
      _building.serviceAndInfra = this.servicesAndInfraComponent.getdata();
    }
    if (this.externalAspectComponent) {
      _building.externalAspectData = this.externalAspectComponent.getData();
    }

    if (this.internalAspectComponent) {
      _building.internalAspectData = this.internalAspectComponent.getData();
    }

    if (this.acGroundsComponent) {
      _building.grounds = {...this.acGroundsComponent.getData(), id: this.acBuilding.grounds.id};
    }
    _building.sizes = this.sizesSubject.value;
    return _building;
  }

  onCoordinatePointsChange(points: CoordinatePoint[]) {
    this._points$.next(points)
  }
  onLandAreasChange(items: LandArea[]) {
    this._landAreas$.next(items)
  }
}
